import { ComponentRef, ViewContainerRef } from '@angular/core';
import { DataboxContainerSupportedTypes } from 'src/app/api/modules/core/dynamic/databoxes/IDataboxContainer';
import { createDigitaServiceError } from 'src/app/app-error';
import { DataboxAttemptsIconComponent } from '../databox-attempts-icon/databox-attempts-icon.component';
import { DataboxHighScoreComponent } from '../databox-highscore/databox-highscore.component';
import { DataboxLivesIconComponent } from '../databox-lives-icon/databox-lives-icon.component';
import { DataboxProgressionIconComponent } from '../databox-progression-icon/databox-progression-icon.component';
import { DataboxProgressionNumericComponent } from '../databox-progression-numeric/databox-progression-numeric.component';
import { DataboxScoreComponent } from '../databox-score/databox-score.component';
import { DataboxTimerComponent } from '../databox-timer/databox-timer.component';

/**
 * A union type representing the set of all possible ComponentRef instances for Databox components.
 * This type is used to define a reference to a component instance that has been dynamically created,
 * which could be any of the Databox components listed below. It is typically used in contexts where
 * a component reference is needed for interaction with the component instance, such as accessing
 * instance properties or invoking methods.
 *
 * The Databox components represented by this type are used to display various kinds of data-related
 * UI elements, such as icons for attempts or lives, numeric progression, scores, and timers.
 */
export type DataboxContainerSupportedComponentReferences =
  | ComponentRef<DataboxAttemptsIconComponent>
  | ComponentRef<DataboxHighScoreComponent>
  | ComponentRef<DataboxLivesIconComponent>
  | ComponentRef<DataboxProgressionIconComponent>
  | ComponentRef<DataboxProgressionNumericComponent>
  | ComponentRef<DataboxScoreComponent>
  | ComponentRef<DataboxTimerComponent>;

/**
 * Dynamically creates and inserts an array of Databox components into a specified ViewContainerRef.
 *
 * This function iterates over a given array of component configurations, instantiates each one,
 * and injects them into the provided ViewContainerRef. It is a higher-level factory function
 * that leverages DataboxContainerFactoryItem for the creation of individual components.
 *
 * @param viewContainerRef - The Angular container where the dynamic components are to be
 * injected. This is typically a reference to a container in the template where the
 * components should be rendered.
 * @param components - An array of component configurations, each conforming to one of the
 * supported Databox component types. These configurations determine the type of component
 * to create and any data or settings that the component requires.
 *
 * @returns An array of ComponentRef instances, each corresponding to a dynamically created
 * Databox component. These references can be used to interact with the components, such as
 * changing inputs or subscribing to outputs.
 */
export const DataboxContainerFactoryArray = (viewContainerRef: ViewContainerRef, components: DataboxContainerSupportedTypes[]) => {
  if (!components || !viewContainerRef) {
    throw createDigitaServiceError(
      `DataboxContainer`,
      `Factory`,
      `The databox container factory was called with invalid parameters`,
      `config`,
    );
  }

  const componentRefs: DataboxContainerSupportedComponentReferences[] = [];

  components.forEach((componentConfig) => {
    const componentRef = DataboxContainerFactoryItem(viewContainerRef, componentConfig);
    componentRefs.push(componentRef);
  });

  return componentRefs;
};

/**
 * Factory function for creating Databox components within a DataboxContainer.
 * It takes a configuration object that specifies the type of Databox component to create
 * and uses the Angular ViewContainerRef to instantiate the component.
 *
 * The `config` union is discriminated by its literal `selector`, so each case
 * receives the correctly narrowed configuration type.
 *
 * @param viewContainerRef - The Angular ViewContainerRef that serves as the container in which the component will be created.
 * @param config - An object conforming to one of the supported Databox component types, containing the necessary configuration for the component.
 * @returns A reference to the newly created Databox component instance.
 *
 * @throws Throws an error if the provided selector is not valid or undefined.
 */
const DataboxContainerFactoryItem = (viewContainerRef: ViewContainerRef, config: DataboxContainerSupportedTypes) => {
  switch (config.selector) {
    default: {
      throw createDigitaServiceError(
        `DataboxContainer`,
        `Factory`,
        `The databox container contained an item with a selector that is not a supported databox component`,
        `config`,
      );
    }
    case 'app-databox-score': {
      const componentReference = viewContainerRef.createComponent(DataboxScoreComponent);
      componentReference.setInput('config', config);
      return componentReference;
    }
    case 'app-databox-highscore': {
      const componentReference = viewContainerRef.createComponent(DataboxHighScoreComponent);
      componentReference.setInput('config', config);
      return componentReference;
    }
    case 'app-databox-timer': {
      const componentReference = viewContainerRef.createComponent(DataboxTimerComponent);
      componentReference.setInput('config', config);
      return componentReference;
    }
    case 'app-databox-progression-numeric': {
      const componentReference = viewContainerRef.createComponent(DataboxProgressionNumericComponent);
      componentReference.setInput('config', config);
      return componentReference;
    }
    case 'app-databox-progression-icon': {
      const componentReference = viewContainerRef.createComponent(DataboxProgressionIconComponent);
      componentReference.setInput('config', config);
      return componentReference;
    }
    case 'app-databox-lives-icon': {
      const componentReference = viewContainerRef.createComponent(DataboxLivesIconComponent);
      componentReference.setInput('config', config);
      return componentReference;
    }
    case 'app-databox-attempts-icon': {
      const componentReference = viewContainerRef.createComponent(DataboxAttemptsIconComponent);
      componentReference.setInput('config', config);
      return componentReference;
    }
  }
};
